// Customer selects a tip percentage

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Lock, ChevronLeft, Edit2 } from 'lucide-react';
import PrimaryLogo from '../../../assets/fonts/Primary logo.png';

// Format currency helper with thousand separators
const formatCurrency = (amount: number, moneda: string = 'DOP') => {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
  return `$${formatted} ${moneda}`
}

// Header Component — large title style to match "Pagar cuenta completa"
const Header: React.FC<{ title: string; onBack?: () => void }> = ({ title, onBack }) => {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-gray-50/80 border-b border-gray-200/50">
      <div className="max-w-lg mx-auto px-4 flex items-center gap-3 py-5 min-h-[4.5rem]">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center justify-center w-10 h-10 -ml-2 rounded-full text-gray-600 hover:bg-gray-200/80 hover:text-gray-900 active:scale-95 transition-all"
            aria-label="Volver"
          >
            <ChevronLeft size={24} strokeWidth={2} />
          </button>
        )}
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight flex-1">
          {title}
        </h1>
      </div>
    </header>
  )
}

// TipSelector Component
interface TipSelectorProps {
  subtotal: number;
  selectedTip: number;
  onSelect: (percentage: number) => void;
  moneda: string;
  customTipAmount: number;
  onCustomTipChange: (amount: number) => void;
}

const TipSelector: React.FC<TipSelectorProps> = ({ subtotal, selectedTip, onSelect, moneda, customTipAmount, onCustomTipChange }) => {
  const tips = [10, 15, 20];
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customInput, setCustomInput] = useState('');
  
  useEffect(() => {
    if (selectedTip === -1 && customTipAmount > 0) {
      setCustomInput(customTipAmount.toString());
      setShowCustomInput(true);
    }
  }, [selectedTip, customTipAmount]);

  const handleCustomClick = () => {
    setShowCustomInput(true);
    onSelect(-1);
  };

  const handleCustomSubmit = () => {
    const amount = parseFloat(customInput) || 0;
    onCustomTipChange(amount);
    setShowCustomInput(false);
  };

  return (
    <div className="space-y-4">
      <div className="text-center py-4">
        <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-0.5">Subtotal</p>
        <p className="text-2xl font-bold text-gray-900 tracking-tight">{formatCurrency(subtotal, moneda)}</p>
      </div>

      <div className="grid grid-cols-3 gap-2.5">
        {tips.map((tip) => {
          const isSelected = selectedTip === tip;
          return (
            <button
              key={tip}
              onClick={() => {
                onSelect(tip);
                setShowCustomInput(false);
              }}
              className={`
                relative p-2.5 rounded-xl border-2 transition-all duration-300 flex items-center justify-center
                ${isSelected 
                  ? 'border-gray-900 bg-gray-900 text-white shadow-lg scale-[1.02]' 
                  : 'border-transparent bg-white text-gray-900 shadow-sm hover:scale-[1.01]'}
              `}
            >
              <span className={`text-[13px] font-bold ${isSelected ? 'text-white' : 'text-gray-900'}`}>{tip}%</span>
            </button>
          )
        })}
      </div>
      
      {showCustomInput ? (
        <div className="bg-white rounded-xl border-2 border-gray-900 p-3 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[13px] font-semibold text-gray-900">Personalizada</span>
            <button
              onClick={() => {
                setShowCustomInput(false);
                onSelect(0);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="text-[11px]">Cerrar</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-500">$</span>
            <input
              type="number"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              placeholder="0.00"
              className="flex-1 text-[13px] font-medium text-gray-900 bg-transparent border-none outline-none"
              autoFocus
            />
            <span className="text-[11px] text-gray-500">{moneda}</span>
          </div>
          <button
            onClick={handleCustomSubmit}
            className="w-full mt-2 py-2 bg-gray-900 text-white rounded-lg text-[11px] font-semibold hover:bg-gray-800 transition-colors"
          >
            Aplicar
          </button>
        </div>
      ) : (
        <button
          onClick={handleCustomClick}
          className={`
            w-full p-3 rounded-xl border-2 transition-all duration-300 flex items-center justify-between px-4
            ${selectedTip === -1
              ? 'border-gray-900 bg-white text-gray-900' 
              : 'border-transparent bg-white text-gray-500 shadow-sm'}
          `}
        >
          <span className="font-semibold text-[13px]">Personalizada</span>
          <Edit2 size={14} className="text-gray-400" />
        </button>
      )}
    </div>
  );
};

const TipSelectionScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mesaId, restaurantId, waiterName = 'tu mesero', staffId, amount, moneda = 'DOP' } = location.state || {};

  const [selectedTip, setSelectedTip] = useState<number>(0);
  const [customTipAmount, setCustomTipAmount] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Calculate tip amount
  const tipAmount = selectedTip === -1 
    ? customTipAmount 
    : selectedTip > 0 
      ? amount * (selectedTip / 100)
      : 0;
  
  const totalWithTip = amount + tipAmount;
  const hasTip = selectedTip > 0 || (selectedTip === -1 && customTipAmount > 0);

  const handleContinue = () => {
    setIsProcessing(true);
    
    // Navigate to payment breakdown screen
    navigate('/payment-breakdown', {
      state: {
        mesaId,
        restaurantId,
        waiterName,
        staffId,
        subtotal: amount,
        tipPercentage: selectedTip === -1 ? 0 : selectedTip,
        tipAmount,
        total: totalWithTip
      }
    });
  };

  const handleContinueWithoutTip = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsProcessing(true);
    const existingState = (location.state || {}) as Record<string, unknown>;
    navigate('/payment-breakdown', {
      state: {
        ...existingState,
        mesaId,
        restaurantId,
        waiterName,
        staffId,
        subtotal: amount,
        tipPercentage: 0,
        tipAmount: 0,
        total: amount
      }
    });
  };

  const handleBack = () => {
    if (mesaId) {
      const s = (location.state || {}) as any
      const paymentMode = s?.paymentMode as undefined | 'full' | 'select' | 'split' | 'fixed'

      // Go back to the correct payment sub-screen when coming from it.
      if (paymentMode === 'fixed') {
        navigate(`/m/${mesaId}`, {
          state: {
            showPaymentSection: true,
            initialPaymentMode: 'fixed',
            fixedAmount: typeof s?.fixedAmount === 'number' ? s.fixedAmount : s?.amount,
          },
        })
        return
      }

      if (paymentMode === 'split') {
        navigate(`/m/${mesaId}`, {
          state: {
            showPaymentSection: true,
            initialPaymentMode: 'split',
            splitCount: typeof s?.splitCount === 'number' ? s.splitCount : 2,
            totalAtTable: typeof s?.totalAtTable === 'number' ? s.totalAtTable : 4,
          },
        })
        return
      }

      if (paymentMode === 'select') {
        navigate(`/m/${mesaId}`, {
          state: {
            showPaymentSection: true,
            initialPaymentMode: 'select',
          },
        })
        return
      }

      // Default: go back to the first screen (Mesa landing).
      navigate(`/m/${mesaId}`)
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F2F7] flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300">
      <Header title="Seleccionar Propina" onBack={handleBack} />

      <main className="max-w-lg mx-auto px-4 py-3 space-y-3 flex-1 w-full">
        <TipSelector 
          subtotal={amount}
          selectedTip={selectedTip}
          onSelect={setSelectedTip}
          moneda={moneda}
          customTipAmount={customTipAmount}
          onCustomTipChange={setCustomTipAmount}
        />

        {/* Buttons — no curve */}
        <div className="space-y-2 pt-2">
          <button
            type="button"
            onClick={handleContinue}
            disabled={isProcessing || !hasTip}
            className={`
              w-full relative h-14 rounded-xl font-semibold shadow-xl shadow-gray-900/15
              flex items-center justify-between px-4 transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)]
              overflow-hidden group
              ${hasTip && !isProcessing
                ? 'bg-[#1C1C1E] text-white hover:scale-[1.01] active:scale-[0.98]' 
                : 'bg-[#E5E5EA] text-gray-400 cursor-not-allowed shadow-none'}
            `}
          >
            <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] ${!hasTip || isProcessing ? 'hidden' : ''}`} />
            {isProcessing ? (
              <div className="w-full flex justify-center">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2.5">
                  <div className="bg-white/10 p-1 rounded-lg">
                    <Lock size={14} className={hasTip ? "text-white" : "text-gray-400"} />
                  </div>
                  <span className="text-[13px] font-medium tracking-wide">Continuar</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-[15px] font-bold tracking-tight">
                    {formatCurrency(totalWithTip, moneda)}
                  </span>
                  <ArrowRight size={16} className="opacity-60 group-hover:translate-x-1 transition-transform" />
                </div>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleContinueWithoutTip}
            disabled={isProcessing}
            className={`
              w-full h-12 rounded-xl font-semibold
              flex items-center justify-center transition-all duration-300
              ${!isProcessing
                ? 'bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 active:scale-[0.98]' 
                : 'bg-[#E5E5EA] text-gray-400 cursor-not-allowed'}
            `}
          >
            <span className="text-[13px] font-medium">Continuar sin propina</span>
          </button>
        </div>
      </main>

      <footer className="pt-4 pb-8 flex items-center justify-center gap-2 bg-[#F2F2F7]">
        <span className="text-[11px] text-[#8E8E93]">powered by</span>
        <img src={PrimaryLogo} alt="YAP" className="h-10 w-auto object-contain" />
      </footer>
      
      {/* Shimmer on primary button — enter animation is global (index.css) */}
      <style>{`
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

export default TipSelectionScreen;
